import { useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import _ from 'lodash';
import { opportunityAttributeLvlElasticity } from 'gqlQueries/opportunityIdentification/attributeLevelElasticityQueries';
import { levelElasticityDriverParser } from 'parser/opportunityIdentificationParser/opportunityIdentificationParcers';

const errorMessage: string = 'Something went wrong!';

interface IGetAttributeLvlElasticityPayload {
    getAttributeLvlElasticity: IGetAttributeLvlElasticity;
}

interface IGetAttributeLvlElasticity {
    attributeData: any[];
    count: number;
}

function useFetchAttributeLevelElasticity() {
    const [error, setError] = useState<any>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState<any>({});
    const offsetRef = useRef(0);

    const [fetchAttributeLevelElasticity] = useLazyQuery(opportunityAttributeLvlElasticity, {
        onCompleted: onFetchComplete,
        onError: onFetchError,
    });

    //called when fetching is complete
    function onFetchComplete(payload: IGetAttributeLvlElasticityPayload) {
        try {
            const { getAttributeLvlElasticity } = payload;
            //if data present parse it
            if (getAttributeLvlElasticity?.attributeData.length > 0) {
                setCount(getAttributeLvlElasticity?.count || 0);

                const parsedData = levelElasticityDriverParser(getAttributeLvlElasticity.attributeData);

                const newMeta = {
                    maxCharacters: Math.max(meta.maxCharacters || 0, parsedData.maxCharacters),
                    minBound: Math.min(meta.minBound || 0, parsedData.minBound),
                    maxBound: Math.max(meta.maxBound || -10, parsedData.maxBound),
                    total: getAttributeLvlElasticity?.count,
                };
                setMeta(newMeta);

                if (offsetRef?.current) {
                    setData([...data, ...parsedData.data]); // swapped the order its working fine
                    return;
                }

                setData([...parsedData?.data]);
                return;
            }
            setMeta({});

            setData(null);
        } catch (error: any) {
            setData(null);
            setError(error);
            console.error(errorMessage, error, 'logging error');
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        console.log('logging error', error);
        //sending empty data to error to avoid null data being sent
        setData(null);
        setLoading(false);
        setError(error?.message);
    }

    return {
        error,
        data,
        loading,
        count,
        meta,
        fetchAttributeLevelElasticity: (variables: any) => {
            offsetRef.current = variables.variables.offset;
            setLoading(true);
            fetchAttributeLevelElasticity(variables);
        },
    };
}

export { useFetchAttributeLevelElasticity };
